import { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPie, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { chartPalette } from "../Inventory/charts/chartPalette";
import { fmtLbs, fmtPct } from "../../utils/inventory/receiverMfcaUtils";
import "../../style/mfca-dashboard.css";

// shared pie sizing — sized so the Material Mix card's natural height is just
// under the Rejection Summary card's natural height. when the row stretches
// to match the taller card (Rejection Summary), the donut centers in the
// freed-up space. avoids leaving a gap inside Rejection Summary.
const PIE_HEIGHT = 100;
const TOP_N = 6;

// roll long tails into "Other" so a 12-material period doesn't end up with
// 12 thin slivers. drives both the pie and the legend.
const rollupTopN = (entries, n) => {
  if (entries.length <= n) return entries;
  const top = entries.slice(0, n);
  const rest = entries.slice(n);
  const otherValue = rest.reduce((s, e) => s + e.value, 0);
  if (otherValue <= 0) return top;
  return [...top, { name: "__OTHER__", value: otherValue, isOther: true, otherCount: rest.length }];
};

const PieSubcard = ({ title, data, totalLabel, totalValue, t, emptyMessage }) => {
  const isEmpty = data.length === 0 || totalValue <= 0;

  return (
    <div className="rl-pie-card">
      <div className="rl-pie-card-header">
        <span className="rl-pie-card-title">{title}</span>
        <span className="rl-pie-card-total">
          <span className="rl-pie-card-total-label">{totalLabel}</span>
          <span className="rl-pie-card-total-value">{fmtLbs(totalValue)} {t("lbs")}</span>
        </span>
      </div>
      <div className="rl-pie-card-body">
        {isEmpty ? (
          <div className="rl-pie-empty">
            <FontAwesomeIcon icon={faChartPie} />
            <span>{emptyMessage}</span>
          </div>
        ) : (
          <>
            <div className="rl-pie-chart">
              <ResponsiveContainer width="100%" height={PIE_HEIGHT}>
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={22}
                    outerRadius={44}
                    paddingAngle={1}
                    stroke="none"
                  >
                    {data.map((entry, i) => (
                      <Cell
                        key={`cell-${i}`}
                        fill={entry.isOther ? "#7f8a99" : chartPalette[i % chartPalette.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value, name, item) => {
                      const pct = totalValue > 0 ? (value / totalValue) * 100 : 0;
                      const label = item?.payload?.isOther
                        ? `${t("Other")} (${item.payload.otherCount} ${t("materials")})`
                        : name;
                      return [`${fmtLbs(value)} ${t("lbs")} · ${fmtPct(pct)}`, label];
                    }}
                    contentStyle={{
                      background: "rgba(20, 20, 22, 0.96)",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      borderRadius: 6,
                      fontSize: "0.8rem",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="rl-pie-legend">
              {data.map((entry, i) => {
                const pct = totalValue > 0 ? (entry.value / totalValue) * 100 : 0;
                const label = entry.isOther
                  ? `${t("Other")} (${entry.otherCount})`
                  : entry.name;
                const swatchColor = entry.isOther ? "#7f8a99" : chartPalette[i % chartPalette.length];
                return (
                  <li key={`leg-${i}`}>
                    <Tooltip
                      title={`${fmtLbs(entry.value)} ${t("lbs")} · ${fmtPct(pct)}`}
                      arrow
                      placement="top"
                    >
                      <span className="rl-pie-legend-chip">
                        <span
                          className="rl-pie-legend-swatch"
                          style={{ background: swatchColor }}
                        />
                        <span className="rl-pie-legend-name">{label}</span>
                        <span className="rl-pie-legend-pct">{fmtPct(pct)}</span>
                      </span>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

PieSubcard.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  totalLabel: PropTypes.string.isRequired,
  totalValue: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string.isRequired,
};

const MaterialMixCharts = ({ perMaterial = [] }) => {
  const { t } = useTranslation();

  // delivered mix — every row contributes its delivered lbs
  const { deliveredData, deliveredTotal } = useMemo(() => {
    const sorted = perMaterial
      .filter((r) => (r.deliveredLbs || 0) > 0)
      .map((r) => ({ name: r.material, value: r.deliveredLbs || 0 }))
      .sort((a, b) => b.value - a.value);
    return {
      deliveredData: rollupTopN(sorted, TOP_N),
      deliveredTotal: sorted.reduce((s, e) => s + e.value, 0),
    };
  }, [perMaterial]);

  // rejected mix — only rows with rejected lbs > 0
  const { rejectedData, rejectedTotal } = useMemo(() => {
    const sorted = perMaterial
      .filter((r) => (r.rejectedLbs || 0) > 0)
      .map((r) => ({ name: r.material, value: r.rejectedLbs || 0 }))
      .sort((a, b) => b.value - a.value);
    return {
      rejectedData: rollupTopN(sorted, TOP_N),
      rejectedTotal: sorted.reduce((s, e) => s + e.value, 0),
    };
  }, [perMaterial]);

  return (
    <div className="mfca-card mfca-card--full rl-material-mix-card">
      <div className="mfca-card-header">
        <div className="mfca-card-icon mfca-icon-blue">
          <FontAwesomeIcon icon={faChartPie} />
        </div>
        <h5 className="mfca-card-title">
          {t("Material Mix")}
          <Tooltip
            title={t("Delivered Mix shows the share of each material in everything you received this period. Rejected Mix shows where the loss came from. Both are weighted by lbs.")}
            arrow
            placement="top"
          >
            <span className="mfca-infotip" role="img" aria-label="info">
              <FontAwesomeIcon icon={faCircleInfo} />
            </span>
          </Tooltip>
        </h5>
      </div>
      <div className="rl-pie-strip">
        <PieSubcard
          title={t("Delivered Mix")}
          data={deliveredData}
          totalLabel={t("Total delivered")}
          totalValue={deliveredTotal}
          t={t}
          emptyMessage={t("No deliveries to chart")}
        />
        <PieSubcard
          title={t("Rejected Mix")}
          data={rejectedData}
          totalLabel={t("Total rejected")}
          totalValue={rejectedTotal}
          t={t}
          emptyMessage={t("Nothing rejected — clean period")}
        />
      </div>
    </div>
  );
};

MaterialMixCharts.propTypes = {
  perMaterial: PropTypes.array,
  darkMode: PropTypes.bool,
};

export default MaterialMixCharts;
